'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherBriefcase, FeatherGavel } from '@subframe/core';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { selectAuthUser } from '@/features/auth/store';
import { carregarContextos, promoverSessao, selectContextosNaoAbertos, selectTemContextoDeParte, selectTrocandoContexto } from '@/features/contextoAcesso/store';
import { SidebarWithCollapsibleSections } from '@/shared/components/ui';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
const ROTA_DO_PORTAL = '/portal/dashboard';
const ROTA_DA_PLATAFORMA = '/';
const propsInterativas = (aoAtivar: () => void): Pick<React.HTMLAttributes<HTMLDivElement>, 'role' | 'tabIndex' | 'onClick' | 'onKeyDown'> => ({
  role: 'button',
  tabIndex: 0,
  onClick: aoAtivar,
  onKeyDown: (evento: React.KeyboardEvent<HTMLDivElement>) => {
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      aoAtivar();
    }
  }
});
interface IrParaPainelDoRequeridoProps {
  isCollapsed?: boolean;
}
interface IconeComAvisoProps {
  pendentes: number;
}
const IconeComAviso: React.FC<IconeComAvisoProps> = t0 => {
  const $ = _c(5);
  const {
    pendentes
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FeatherGavel />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pendentes) {
    t2 = pendentes > 0 && <span data-testid="alternar-painel-aviso" className="bg-error-600 absolute -top-2 -right-2 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-bold text-white ring-2 ring-white">{pendentes > 9 ? "9+" : pendentes}</span>;
    $[1] = pendentes;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="relative inline-flex flex-none">{t1}{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const IrParaPainelDoRequerido: React.FC<IrParaPainelDoRequeridoProps> = t0 => {
  const $ = _c(14);
  const {
    isCollapsed: t1
  } = t0;
  const isCollapsed = t1 === undefined ? false : t1;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const temContextoDeParte = useAppSelector(selectTemContextoDeParte);
  const naoAbertos = useAppSelector(selectContextosNaoAbertos);
  let t2;
  let t3;
  if ($[0] !== dispatch) {
    t2 = () => {
      dispatch(carregarContextos());
    };
    t3 = [dispatch];
    $[0] = dispatch;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (!temContextoDeParte) {
    return null;
  }
  const pendentes = naoAbertos.length;
  const t4 = isCollapsed ? undefined : "h-9";
  let t5;
  if ($[3] !== pendentes) {
    t5 = <IconeComAviso pendentes={pendentes} />;
    $[3] = pendentes;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = pendentes > 0 ? `Painel do requerido — ${pendentes} não vistos` : "Painel do requerido";
  let t7;
  if ($[5] !== router) {
    t7 = propsInterativas(() => router.push(ROTA_DO_PORTAL));
    $[5] = router;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = isCollapsed ? "Requerido" : "Painel do requerido";
  let t9;
  if ($[7] !== isCollapsed || $[8] !== t4 || $[9] !== t5 || $[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
    t9 = <SidebarWithCollapsibleSections.NavItem className={t4} data-testid="alternar-painel-requerido" icon={t5} isCollapsed={isCollapsed} title={t6} {...t7}>{t8}</SidebarWithCollapsibleSections.NavItem>;
    $[7] = isCollapsed;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};
export const VoltarParaPlataforma: React.FC = () => {
  const $ = _c(9);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectAuthUser);
  const promovendo = useAppSelector(selectTrocandoContexto);
  let t0;
  if ($[0] !== dispatch) {
    t0 = async () => {
      const resultado = await dispatch(promoverSessao());
      if (promoverSessao.fulfilled.match(resultado)) {
        window.location.assign(ROTA_DA_PLATAFORMA);
      }
    };
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const voltar = t0;
  if (!user || user.tipoUsuario === "NOTIFICADO" || !user.hasEscritorio) {
    return null;
  }
  let t1;
  if ($[2] !== voltar) {
    t1 = () => void voltar();
    $[2] = voltar;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-xs font-medium text-[--color-text-label-muted]">Painel do escritório</span>;
    t3 = <FeatherBriefcase className="h-4 w-4 text-[--color-text-label-muted]" />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== promovendo || $[7] !== t1) {
    t4 = <button type="button" data-testid="alternar-painel-plataforma" disabled={promovendo} onClick={t1} title={"Voltar para o painel do escrit\xF3rio"} className="flex shrink-0 items-center gap-1 rounded-full border border-neutral-200 bg-white p-2 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] disabled:opacity-60">{t2}{t3}</button>;
    $[6] = promovendo;
    $[7] = t1;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};